import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import { _ } from '_locales';
import { colors } from '_styles';
import { goToTop, eventTracker } from '_helpers';

import { cts_secure } from '_cts';
import apis from '_apis';

import { Input } from '_components';
import Loader from './Loader';
import { isExceptionalWord, mapInputValue } from './helpers';

/**
 * import styles
 */
const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const Container = styled.div`
  background-color: #fff;
  padding: 45px 35px;
  box-sizing: border-box;
`;

const SearchInputContainer = styled.div`
  width: calc(100% - 185px);
  float: left;
  margin-top: 3px;
  border-radius: 15px 0 0 15px;

  input {
    border-radius: 15px 0 0 15px;

    &:focus {
      outline: none !important;
      box-shadow: 0 0 2px 2px #51a7e8;
      border-radius: 15px 0 0 15px;
    }
  }

  @media screen and (max-width: 500px) {
    width: 100%;
    border-radius: 15px;
    float: initial;

    input {
      border-radius: 15px;

      &:focus {
        outline: none !important;
        box-shadow: 0 0 2px 2px #51a7e8;
        border-radius: 15px;
      }
    }
  }

  ${(parentWidth < 501) && css`
    width: 100%;
    border-radius: 15px;
    float: initial;

    input {
      border-radius: 15px;

      &:focus {
        outline: none !important;
        box-shadow: 0 0 2px 2px #51a7e8;
        border-radius: 15px;
      }
    }
  `};
`;

const ConfirmInputContainer = styled.div`
  width: 185px;
  border-radius: 0 15px 15px 0;
  margin-top: 3px;
  display: inline-block;


  input {
    border-radius: 0 15px 15px 0;

    &:focus {
      outline: none !important;
      box-shadow: 0 0 2px 2px #51a7e8;
    }
  }

  @media screen and (max-width: 500px) {
    display: none;

    label,
    input {
      display: none;
    }
  }

  ${(parentWidth < 501) && css`
    display: none;

    label,
    input {
      display: none;
    }
  `};
`;

const inputStyle = {
  width: '100%',
  border: 'none',
  backgroundColor: '#f3f2f2',
  fontSize: '14px',
  color: '#000',
  fontWeight: '400',
  padding: '23px',
  boxSizing: 'border-box',
  lineHeight: '15px',
};

const submitInputStyle = {
  width: '100%',
  border: 'none',
  fontSize: '14px',
  color: '#f3f2f2',
  fontWeight: '400',
  padding: '23px 9px',
  boxSizing: 'border-box',
  margin: '0',
  lineHeight: '0',
  cursor: 'pointer',
};

const InputHolder = styled.div`
    margin: 0 auto;
    max-width: 577px;
    position: relative;
`;

const ListContainerStyle = {
  maxWidth: '577px',
  margin: '17px auto',
  boxSizing: 'border-box',
  minHeight: '228px',
  padding: '0',
};

const MaterialItem = styled.button`
  text-align: left;
  display: block;
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 10px 0;
  font-size: 15px !important;
  width: 100%;
  border: none;
  background-color: transparent;
  padding-left: 10px;
  border-radius: 5px;
  transition: 0.3s;

  color: ${colors.RC_ACTION} !important;
  text-decoration: none !important;

  &:hover {
    color: ${colors.RC_ACTION} !important;
    text-decoration: none !important;
    background-color: #EEEDEE;
  }
`;

const Synonym = styled.span`
  display: inline-block;
  padding: 0;
  margin: 0;
  color: #000;
  &:before{ content: ' (' }
  &:after{ content: ') ' }
`;

const Label = styled.p`
   font-size: 14px !important;
   font-weight: 400 !important;
   line-height: 1.36;
   margin: 0;
   margin-bottom: 10px;
   color: #0a1514;
   display: block;

  @media screen and (max-width: 500px) {
    text-align: center;
    font-weight: 500 !important;
    font-size: 20px !important;
     
    margin-bottom: 60px;
  }

  ${(parentWidth < 501) && css`
    text-align: center;
    font-weight: 500 !important;
    font-size: 20px !important;
     
    margin-bottom: 60px;
  `};
`;

const NothingFound = styled.div`
  width: 100%;
  height: 228px;
  text-align: center;
  background-color: #fff;
  max-width: 577px;
  margin: 17px auto;
  box-sizing: border-box;
`;

// End styles

const WGW = ({ config, props, colors: configColors }) => {
  const {
    districtId, localityId, selectedLang, webappConfig
  } = config;
  const { wgw_placeholder: wgwPlaceholder, has_wgw: hasWgw, wgw_dscr: wgwLabel } = webappConfig || { wgw_placeholder: '', has_wgw: false, wgw_dscr: '' };
  const { main_color = '' } = configColors;

  const [term, setTerm] = useState('');
  const [list, setList] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const { history } = props;

  useEffect(() => {
    eventTracker('Plugin Visit', { action: 'what-goes-where', title: 'whatGoesWhere' });
  }, [districtId]);

  useEffect(() => {
    if (term.length < 4) {
      setList([]);
      setError(false);
      setLoading(false);
    }
  }, [term]);

  const fetchResult = async (term) => {
    try {
      setLoading(true);
      const { data, status } = await cts_secure('get', apis.searchMaterialByTerm(localityId, term, selectedLang));

      if (
        !data
        && status !== 200
      ) throw new Error('Error fetching data in search Material by term.');

      if (
        data
        && data.header.success
        && data.content.results.length
      ) {
        setList(data.content.results);
        setError(false);
      } else {
        setList([]);
        setError(true);
      }

      if (!term.length) setError(false);

      return undefined;
    } catch (e) {
      return Promise.reject(e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
  * Fix race condition problem
  * @param {string} term
  */
  const [debouncedFetchResult] = useDebouncedCallback(
    fetchResult,
    1000
  );

  /**
   * Get the character from input. Using debounce to prevent the race condition
   * @param {Text} target
  */
  const onChange = async ({ value }) => {
    setTerm(value);
    if (value.length > 3 || isExceptionalWord(value)) {
      debouncedFetchResult(mapInputValue(value));
    }
  };

  const materialItemClickHandler = (matId, synonym) => {
    goToTop();
    history.push('/wgw/material-details', { matId });
    eventTracker('WGW_MAT_HIT', { keyword: term, mat_id: matId, matched_synonym: synonym });
  };

  return (
    <Container>
      <InputHolder>
        <Label>{(hasWgw && wgwLabel.length) ? wgwLabel : _('wgw.search_for_recycling_and_disposal_information')}</Label>
        <SearchInputContainer>
          <label htmlFor="wgw-search" style={{ display: 'none' }}>
            {_('wgw.search_for_recycling_and_disposal_information')}
          </label>
          <Input
            type="text"
            id="wgw-search"
            style={inputStyle}
            onChange={({ target }) => onChange(target)}
            placeholder={(hasWgw && wgwPlaceholder.length) ? wgwPlaceholder : _('wgw.what_goes_where_eg_batteries')}
          />
        </SearchInputContainer>

        <ConfirmInputContainer>
          <label htmlFor="submit-wgw" style={{ display: 'none' }}>{_('wgw.search')}</label>
          <Input
            type="submit"
            id="submit-wgw"
            style={{
              ...submitInputStyle,
              backgroundColor: (main_color ? `#${main_color}` : colors.RC_ACTION)
            }}
            value={_('wgw.search')}
            ariaLabel={_('wgw.search')}
          />
        </ConfirmInputContainer>
      </InputHolder>

      {loading && (<Loader />)}

      {
        !loading
          && list.length ? (
          <Container width="100%" style={ListContainerStyle}>
            {
              list
                .filter((item, index) => index < 10)
                .map(({ id, name, synonym }) => (
                  <MaterialItem
                    key={id}
                    onClick={() => materialItemClickHandler(id, synonym)}
                  >
                    {name}
                    {' '}
                    {(synonym) ? (
                      <Synonym>
                        {synonym}
                      </Synonym>
                    ) : null}
                  </MaterialItem>
                ))

            }
          </Container>
        ) : null
      }

      {(
        error
        && !loading

      ) && (<NothingFound>{_('wgw.result_not_found')}</NothingFound>)}
    </Container>
  );
};

export default connect(
  (store) => ({
    colors: store.config.templateColour ? JSON.parse(store.config.templateColour) : {},
  })
)(WGW);
